import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';

import { transcriptMetadataOptions, useDeleteTranscriptOptions } from '@/entities/transcript';

import { TranscriptButton } from './transcript/transcript-button';
import { TranscriptContent } from './transcript/transcript-content';
import { TranscriptDetailsDialog } from './transcript/transcript-details-dialog';
import { TranscriptDetailsPopover } from './transcript/transcript-details-popover';
import { TranscriptFooter } from './transcript/transcript-footer';
import { TranscriptUnavailablePopover } from './transcript/transcript-unavailable-popover';

type Props = {
    transcriptId: string;
    deletedAt?: string | null;
    isAutoDeleted?: boolean | null;
};

export function Transcript({ transcriptId, deletedAt, isAutoDeleted }: Props) {
    const { data: metadata, isPending: isMetadataPending } = useQuery(
        transcriptMetadataOptions(transcriptId),
    );
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const isXs = useBreakpointDown('xs');

    const { mutate: deleteTranscript, isPending: isPendingDelete } = useMutation(
        useDeleteTranscriptOptions(transcriptId),
    );

    function handleDelete() {
        deleteTranscript(undefined, { onSuccess: () => setIsDeleteOpen(false) });
    }

    const isUnavailable = !isMetadataPending && !metadata;

    return match({ isMetadataPending, isUnavailable, isXs })
        .with({ isMetadataPending: true }, () => <TranscriptButton disabled />)
        .with({ isUnavailable: true }, () => (
            <TranscriptUnavailablePopover
                open={isOpen}
                onOpen={setIsOpen}
                deletedAt={deletedAt}
                isAutoDeleted={isAutoDeleted}
                trigger={<TranscriptButton />}
            />
        ))
        .with({ isXs: true }, () => (
            <TranscriptDetailsDialog
                open={isOpen}
                onOpen={setIsOpen}
                trigger={<TranscriptButton />}
            >
                <div className="flex-1 min-h-0 overflow-y-auto px-6">
                    <TranscriptContent transcriptId={transcriptId} />
                </div>
                <TranscriptFooter
                    isOpen={isDeleteOpen}
                    onOpenChange={setIsDeleteOpen}
                    onDelete={handleDelete}
                    expiresAt={metadata?.expiresAt}
                    isPendingDelete={isPendingDelete}
                />
            </TranscriptDetailsDialog>
        ))
        .with({ isXs: false }, () => (
            <TranscriptDetailsPopover open={isOpen} onOpen={setIsOpen}>
                <div className="flex-1 min-h-0 overflow-y-auto px-6">
                    <TranscriptContent transcriptId={transcriptId} />
                </div>
                <TranscriptFooter
                    isOpen={isDeleteOpen}
                    onOpenChange={setIsDeleteOpen}
                    onDelete={handleDelete}
                    expiresAt={metadata?.expiresAt}
                    isPendingDelete={isPendingDelete}
                />
            </TranscriptDetailsPopover>
        ))
        .exhaustive();
}
